import { BaseEffect } from '../BaseEffect.js';

/**
 * Outrun effect implementation based on "Outrun The Rain" by Ian Reichert-Watts
 * Original: https://www.shadertoy.com/view/ltccRl
 */
export class OutrunEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            sunColorR: 0.79,
            sunColorG: 0.17,
            sunColorB: 0.32,
            gridColorR: 0.22,
            gridColorG: 0.0,
            gridColorB: 0.32,
            accentColorR: 0.0,
            accentColorG: 1.0,
            accentColorB: 0.75,
            sunX: 0.0,                         // Sun X position (-1 to 1)
            sunY: -0.13,                       // Sun Y position (-1 to 1)
            gridDensity: 0.42,                 // Controls grid line density
            sunBurstIntensity: 0.1,            // Intensity of sun burst effect
            gridIntensity: 0.0001,             // Grid color intensity multiplier
            opacity: 0.5,                      // Effect opacity
            speed: 1.0,                        // Animation speed multiplier
            animate: true
        };

        super('outrun', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision mediump float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform vec3 u_sunColor;
            uniform vec3 u_gridColor;
            uniform vec3 u_accentColor;
            uniform float u_sunX;
            uniform float u_sunY;
            uniform float u_gridDensity;
            uniform float u_sunBurstIntensity;
            uniform float u_gridIntensity;
            uniform float u_opacity;
            uniform float u_speed;
            
            varying vec2 v_texcoord;

            const float PI = 3.14159;
            const float TAU = PI * 2.0;
            const int STEPS = 128;
            const float STEP_SIZE = 0.42;
            const float T_MAX = float(STEPS) * STEP_SIZE;

            float floorHeight(vec3 p) {
                // Fixed wave parameters for a good-looking floor
                float slowWave = sin(p.z * 0.0004) * 0.2;
                float mediumWave = sin(p.z * 0.008) * 0.4;
                float fastWave = sin(p.x * 0.42 + sin(p.z * 0.00004) * 420.0) * 0.3;
                return slowWave + mediumWave + fastWave - 1.0;
            }

            float floorHeightRender(vec3 p) {
                float height = floorHeight(p);
                vec2 point = u_resolution.xy * 0.5;
                vec2 coord = floor(p.xz/0.1) * 0.1;
                height += sin(length(coord-point) * (10000.0 + u_time * 0.02)) * 0.1;
                return height;
            }

            vec4 render(vec3 rayOrigin, vec3 rayDir) {
                vec4 col = vec4(0.0);
                
                // Sun direction from X,Y position
                vec3 sunDir = normalize(vec3(u_sunX, u_sunY, -1.0));
                
                // Sun effects
                float sunDot = dot(rayDir, -sunDir);
                col.rgb = max(col.rgb, 0.1 * abs(sin(u_time + sunDot * 42.0)) * u_sunColor);
                col.rgb += vec3(pow(sunDot, 42.0) * 0.42) * u_accentColor;
                
                // Sun body
                float sunAlpha = clamp(sunDot - 0.99, 0.0, 1.0);
                vec3 rayDown = cross(vec3(1.0, 0.0, 0.0), rayDir);
                sunAlpha *= clamp(cos(PI * 2.9 * clamp(dot(rayDown, sunDir) * 20.0 + 0.2, 0.0, 42.0)), 0.0, 1.0);
                col.rgb = max(col.rgb, 200.0 * sunAlpha * u_sunColor);
                
                // Sun burst
                float burstAngle = atan(rayDir.y - u_sunY, rayDir.x - u_sunX) * 8.0;
                float burstEffect = abs(sin(burstAngle)) * pow(sunDot, 8.0) * u_sunBurstIntensity;
                col.rgb += burstEffect * u_accentColor;
                
                float t = STEP_SIZE;
                for(int i = 0; i < STEPS; i++) {
                    vec3 p = rayOrigin + (rayDir * t);
                    float depth = (t/T_MAX);
                    float distFade = pow(1.0 - depth, 2.0);
                    float delta = p.y - floorHeightRender(p);
                    
                    // Floor
                    float alpha = pow(clamp(1.0 - abs(delta), 0.0, 1.0), 42.0);
                    float gridX = pow(abs(sin(p.x + sin(p.z * 0.033) * 6.4)), 1.42);
                    float gridZ = pow(abs(sin(p.z * u_gridDensity) + sin(p.x * 0.013) * 0.2), 20.0);
                    col.rgb = max(col.rgb, alpha * gridX * u_sunColor);
                    col.rgb = max(col.rgb, alpha * gridZ * u_sunColor);
                    float lightX = pow(abs(sin(p.x * 0.064) * 10.2), 0.42);
                    col.rgb += 0.015 * (pow(alpha, 0.2) * (lightX - gridZ) * u_accentColor) * distFade;
                    
                    // Atmosphere
                    float bandFreq = 0.42;
                    float band;
                    if(delta > 0.0) {
                        band = sin(p.z - p.y * bandFreq) + cos(p.z * bandFreq);
                    } else {
                        band = sin(p.z + p.y * bandFreq) + cos(p.z * bandFreq);
                    }
                    band += 1.0 - clamp(p.y * 0.8, 0.0, 1.0);
                    vec3 cloud = vec3(gridZ + band, abs(gridZ + band), gridZ * band);
                    col.rgb += 0.0042 * (1.0 - alpha) * cloud * u_sunColor;
                    col.rgb += 0.01 * clamp(p.y * 0.03, 0.0, 1.0) * u_accentColor;
                    
                    // Fog with intensity control
                    col.rgb += u_gridColor * u_gridIntensity;
                    
                    t += STEP_SIZE;
                }
                
                return col;
            }
            
            void main() {
                vec2 uv = v_texcoord;
                vec2 centeredCoord = (gl_FragCoord.xy - (u_resolution.xy * 0.5)) / u_resolution.x;
                
                // Camera setup - only apply speed to forward movement
                vec3 rayOrigin = vec3(0.0, 0.0, u_time * 20.0 * u_speed);
                float floorY = floorHeight(rayOrigin);
                rayOrigin.y = floorY * 0.9 + 0.2;
                
                // Create ray direction
                vec3 rayDir = normalize(vec3(centeredCoord, 1.0));
                
                // Render the scene
                vec4 outrunEffect = render(rayOrigin, rayDir);
                
                // Mix with original image using opacity
                vec4 originalColor = texture2D(u_image, uv);
                gl_FragColor = mix(originalColor, outrunEffect, u_opacity);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_sunColor', { type: '3fv' });
        this.addUniform('u_gridColor', { type: '3fv' });
        this.addUniform('u_accentColor', { type: '3fv' });
        this.addUniform('u_sunX', { type: '1f' });
        this.addUniform('u_sunY', { type: '1f' });
        this.addUniform('u_gridDensity', { type: '1f' });
        this.addUniform('u_sunBurstIntensity', { type: '1f' });
        this.addUniform('u_gridIntensity', { type: '1f' });
        this.addUniform('u_opacity', { type: '1f' });
        this.addUniform('u_speed', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        Object.assign(uniforms, {
            u_sunColor: { 
                value: [this.params.sunColorR, this.params.sunColorG, this.params.sunColorB],
                type: '3fv'
            },
            u_gridColor: { 
                value: [this.params.gridColorR, this.params.gridColorG, this.params.gridColorB],
                type: '3fv'
            },
            u_accentColor: { 
                value: [this.params.accentColorR, this.params.accentColorG, this.params.accentColorB],
                type: '3fv'
            },
            u_sunX: { value: this.params.sunX, type: '1f' },
            u_sunY: { value: this.params.sunY, type: '1f' },
            u_gridDensity: { value: this.params.gridDensity, type: '1f' },
            u_sunBurstIntensity: { value: this.params.sunBurstIntensity, type: '1f' },
            u_gridIntensity: { value: this.params.gridIntensity, type: '1f' },
            u_opacity: { value: this.params.opacity, type: '1f' },
            u_speed: { value: this.params.speed, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            opacity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Opacity'
            },
            speed: {
                min: 0,
                max: 5.0,
                step: 0.1,
                label: 'Speed'
            },
            sunX: {
                min: -1,
                max: 1,
                step: 0.01,
                label: 'Sun Position X'
            },
            sunY: {
                min: -1,
                max: 1,
                step: 0.01,
                label: 'Sun Position Y'
            },
            sunBurstIntensity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Sun Burst Intensity'
            },
            gridIntensity: {
                min: 0,
                max: 0.001,
                step: 0.00001,
                label: 'Grid Intensity'
            },
            sunColorR: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Sun Color R'
            },
            sunColorG: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Sun Color G'
            },
            sunColorB: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Sun Color B'
            },
            gridColorR: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Grid Color R'
            },
            gridColorG: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Grid Color G'
            },
            gridColorB: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Grid Color B'
            },
            accentColorR: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Accent Color R'
            },
            accentColorG: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Accent Color G'
            },
            accentColorB: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Accent Color B'
            },
            gridDensity: {
                min: 0.1,
                max: 1.0,
                step: 0.01,
                label: 'Grid Density'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 